import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { CancellationMode, CancellationState, OrderState, ReceiptType } from "@/data/model/Enums";
import type { IOperatorFulfillment } from "@/data/model/Operator";
import type { IFullOrder, IVatGroupDto } from "@/data/model/Order";



import { BaseModal } from "@/shared/elements/BaseModal";
import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { toMoney } from "@/shared/utils/format";
import { createEmptyDiv, createVueComponent } from "@/shared/utils/vue";



import { CancellationPositionsWindow } from "@/components/dialog/CancellationPositionsWindow";
import { CancellationWindow } from "@/components/dialog/CancellationWindow";
import { Popup } from "@/components/dialog/Popup";
import CancelWholeOrderModal from "@/components/order/CancelWholeOrderModal.vue";
import EditPreorderPopup from "@/components/order/EditPreorderPopup.vue";
import DeliveryAddressForm from "@/components/pay/DeliveryAddressForm.vue";



import { OperatorClient } from "@/operator/OperatorClient";
import OrderInfoHeader from "@/operator/components/order/OrderInfoHeader.vue";
import { ProductionContext } from "@/operator/context/ProductionContext";
import { OrderItemsList } from "@/operator/elements/OrderItemList";
import { type OpenBillTableSelectedEventData, operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";
import { SendOrderReceiptWindow } from "@/operator/modal//SendOrderReceiptWindow";
import { PrintOrderReceiptWindow } from "@/operator/modal/PrintOrderReceiptWindow";


export class OrderDetailsModal extends BaseModal {
    private productionContext: ProductionContext;

    private $head?: JQuery;
    private $summaryHeader?: JQuery;
    private $itemList?: JQuery;
    private $totals?: JQuery;
    private $editOrderButton?: JQuery | null;
    private $receiptDropDown?: JQuery | null;
    private $cancelOrderBtn?: JQuery | null;

    private readonly orderDao: OrderDao;
    private readonly customerDao: CustomerDao;
    private readonly orderId: string;
    private orderDetails?: IFullOrder;

    private isInitialized?: boolean;
    private orderInfoHeaderComponent: any;
    private onOrderCancelled: () => void;

    constructor(orderId: string, onOrderCancelled: () => void) {
        super(undefined, undefined, false, true);

        this.orderId = orderId;
        this.onOrderCancelled = onOrderCancelled;

        this.productionContext = ProductionContext.getInstance();
        this.orderDao = Factory.getInstance<OrderDao>(OrderDao);
        this.customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

        this.$element.addClass("orderdetails");

        this.loadOrder(orderId);
    }

    private async loadOrder(id: string) {
        const orderDetails = await this.orderDao.getSingle(id, { showLocationOrderItemsIfLocationIsNotOwner: false });
        this.orderDetails = orderDetails;
        await this.updateModalContent(orderDetails);
    }

    private async updateModalContent(order: IFullOrder) {
        // if it's the first time - initialize containers
        if (!this.isInitialized) {
            this.$body.addClass("scrollable");
            this.$head = $('<div class="head" />').appendTo(this.$header);
            this.$itemList = $('<div class="itemlist" />').appendTo(this.$body);
            this.$totals = $('<div class="totals" />').appendTo(this.$footer!);
            this.isInitialized = true;
        }

        if (this.$editOrderButton != null) {
            this.$editOrderButton.remove();
            this.$editOrderButton = null;
        }

        if (order.canOrderBeEditedByOperator) {
            this.$editOrderButton = $(
                `<div><button class="btn btn-success ml-2" type="button" id="edit-order-btn">${trans.orderDetails.edit}</div></button></div>`
            );
            this.$editOrderButton.appendTo(this.$footer!);

            this.$editOrderButton.click((e) => {
                e.preventDefault();
                this.openEditOrderPopup(this.orderId);
            });
        }

        if (this.$receiptDropDown != null) {
            this.$receiptDropDown.remove();
            this.$receiptDropDown = null;
        }

        if (this.canPrintReceipt(order)) {
            this.$receiptDropDown = $(
                '<div><button class="btn btn-success ml-2 dropdown-toggle" type="button" id="receiptbtn" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">' +
                    trans.orderDetails.receipt +
                    '<div class="dropdown-menu" aria-labelledby="receiptbtn"><a class="dropdown-item" href="#" data-action="sendreceipt">' +
                    trans.orderDetails.sendReceipt +
                    '</a><a class="dropdown-item" href="#" data-action="printreceipt">' +
                    trans.orderDetails.printReceipt +
                    "</a></div></button></div>"
            );
            this.$receiptDropDown.appendTo(this.$footer!);

            this.$element.find("#receiptbtn > .dropdown-menu > .dropdown-item").click((e) => {
                e.preventDefault();

                switch ($(e.currentTarget).attr("data-action")) {
                    case "sendreceipt":
                        this.openSendReceipt();
                        this.close();
                        break;
                    case "printreceipt":
                        this.showPrintOrderReceipt();
                        this.close();
                        break;
                }
            });
        }

        if (this.$cancelOrderBtn != null) {
            this.$cancelOrderBtn.remove();
            this.$cancelOrderBtn = null;
        }

        if (order.cancellationState !== CancellationState.Cancelled) {
            const canOrderBeCanceled = await this.canOrderBeCanceled(order.id, order.locationId);
            if (canOrderBeCanceled) {
                if (this.$footer?.find("#cancel-order-btn")) {
                    this.$footer?.find("#cancel-order-btn").parent().remove();
                    this.$cancelOrderBtn = null;
                }
                this.$cancelOrderBtn = $(
                    `<div><button class="btn btn-danger ml-2 dropdown-toggle" type="button" id="cancel-order-btn" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">${trans.cancellation.cancel}<div class="dropdown-menu" aria-labelledby="cancel-order-btn">${
                        order.cancellationState == null
                            ? `<a class="dropdown-item" href="#" data-action="whole-order">${trans.enums.cancellationMode[CancellationMode[CancellationMode.WholeOrder]]}</a>`
                            : ""
                    }<a class="dropdown-item" href="#" data-action="positions">${trans.enums.cancellationMode[CancellationMode[CancellationMode.Positions]]}</a></div></button></div>`
                );

                this.$cancelOrderBtn.appendTo(this.$footer!);

                this.$element.find("#cancel-order-btn > .dropdown-menu > .dropdown-item").click((e) => {
                    e.preventDefault();

                    switch ($(e.currentTarget).attr("data-action")) {
                        case "whole-order":
                            this.openCancelWholeOrderModal(order);
                            break;
                        case "positions":
                            this.openCancellation(order.payLater, CancellationMode.Positions);
                            break;
                    }
                });
            }
        }

        // cleanup previous data
        this.$head!.empty();
        this.$itemList!.empty();
        this.$totals!.empty();

        // set header info
        this.$summaryHeader = $('<div class="order-summary-header" />').appendTo(this.$head!);
        this.initOrderInfoHeaderComponent(order);

        // set order items list
        OrderItemsList.render(this.$itemList!, order.items, true);

        // display discount
        if (order.currentOrderDiscountAmount != 0) {
            $(
                `<div class='totals-item'><div>${trans.orderDetails.discount}</div><div style="color: #fd397a">
                ${(-1 * order.currentOrderDiscountAmount).toFixed(2)}
                </div></div>`
            ).appendTo(this.$totals!);
        }

        // set vat groups in summary
        for (const vatGroup of order.currentVatGroups) {
            this.$totals!.append(
                `<div class="totals-item">
                    <div>${this.getVatGroupTitle(vatGroup)}</div>
                    <div>${toMoney(vatGroup.totalAmount, true)}</div>
                </div>`
            );
        }

        // display fees
        const $fees = $(`<div class="totals-item"><div> ${translations.pay.fees} </div><div>${toMoney(order.deliveryFee, true)}</div></div>`);
        this.$totals!.append($fees);


        // display tip
        if (order.tip) {
            const $tip = $(
                `<div class="totals-item"><div> ${trans.orderDetails.tip} </div><div>${toMoney(order.tip, true)}</div></div>`
            );
            if (order.isTipCancelled) {
                $tip.addClass("cancelled");
            }
            this.$totals!.append($tip);
        }

        // display summary
        $(
            `<div class='total totals-item'><div>${trans.orderDetails.total}</div><div>
            ${toMoney(order.totalPaymentAmountFromPaymentDrafts, true)}
            </div></div>`
        ).appendTo(this.$totals!);

        if (order.cancellationState != null) {
            $(
                `<div class='totals-item'><div>${trans.orderDetails.totalCancelled}</div><div style="color: #fd397a">
                    ${toMoney(-1 * order.totalCancellationAmount, true)}
                    </div></div>`
            ).appendTo(this.$totals!);
        }
    }

    public openCancellation(payLater: boolean, mode: CancellationMode) {
        if (payLater && mode == CancellationMode.Positions) {
            const m = new CancellationPositionsWindow(
                this.orderId,
                OperatorClient.context.isAdmin(),
                true,
                () => {
                    this.onOrderCancelled();
                    this.loadOrder(this.orderId);
                },
                () => {}
            );
            m.show();
        } else {
            const m = new CancellationWindow(
                this.orderId,
                OperatorClient.context.isAdmin(),
                true,
                mode,
                () => {
                    this.onOrderCancelled();
                    this.loadOrder(this.orderId);
                },
                () => {}
            );
            m.show();
        }
    }

    private canPrintReceipt(order: IFullOrder): boolean {
        return (
            order != null &&
            !order.payLater &&
            (order.state === OrderState.FullyPaid || order.state === OrderState.Closed)
        );
    }

    private getVatPercentValueAsString(vat: number): string {
        return (Math.round(vat * 100000) / 1000).toString();
    }

    protected onShow() {
        operatorEvents.productionContext.FulfillmentChanged.add(this.onFulfillmentUpdated, this);
        operatorEvents.productionContext.LinkedFulfillmentsChanged.add(this.onLinkedFulfillmentsUpdated, this);
        operatorEvents.productionContext.FulfillmentRemoved.add(this.onFulfillmentUpdated, this);
        operatorEvents.orderDetailsModal.openBillTableSelected.add(this.onOpenBillTableSelected, this);
        operatorEvents.orderDetailsModal.showDeliveryAddressClicked.add(this.openDeliveryAddressPopup, this);
        operatorEvents.modals.closeAllModals.add(this.close, this);
    }

    protected onHide() {
        operatorEvents.productionContext.FulfillmentChanged.remove(this.onFulfillmentUpdated, this);
        operatorEvents.productionContext.LinkedFulfillmentsChanged.remove(this.onLinkedFulfillmentsUpdated, this);
        operatorEvents.productionContext.FulfillmentRemoved.remove(this.onFulfillmentUpdated, this);
        operatorEvents.orderDetailsModal.openBillTableSelected.remove(this.onOpenBillTableSelected, this);
        operatorEvents.orderDetailsModal.showDeliveryAddressClicked.remove(this.openDeliveryAddressPopup, this);
        operatorEvents.modals.closeAllModals.remove(this.close, this);
    }

    private onOpenBillTableSelected(eventData: OpenBillTableSelectedEventData) {
        operatorEvents.modals.closeAllModals.dispatch();
        operatorEvents.showOrderAndPayView.dispatch({
            tableId: eventData.tableId,
            billId: eventData.billId,
        });
    }

    private async openDeliveryAddressPopup(orderId: string) {

        const deliveryAddress = await Factory.getInstance<OrderDao>(OrderDao).getOrderDeliveryAddress(orderId, {});

        const popup = Popup.show({
            component: DeliveryAddressForm,
            componentProps: {
                deliveryAddress: deliveryAddress,
                hide: () => {
                    popup?.close();
                },
            },
            showConfirmButton: false,
            closePopupOnOverlayClick: true,
            showCloseButton: true,
            title: translations.deliveryAddress.deliveryAddress,
            zIndex: 10000
        }, this.$element.parent());
    }

    private showPrintOrderReceipt() {
        const m = new PrintOrderReceiptWindow(this.orderId, ReceiptType.Order);
        m.show();
    }

    public openSendReceipt() {
        const m = new SendOrderReceiptWindow(this.orderId, ReceiptType.Order);
        m.show();
    }

    private onFulfillmentUpdated(fulfillment: IOperatorFulfillment) {
        if (fulfillment.orderId == this.orderId) {
            this.reloadCurrentOrder();
        }
    }

    private onLinkedFulfillmentsUpdated(fulfillments: IOperatorFulfillment[]) {
        if (fulfillments.length > 0) {
            const orderId = fulfillments[0].orderId;
            if (orderId == this.orderId) {
                this.reloadCurrentOrder();
            }
        }
    }

    private getVatGroupTitle(vatGroup: IVatGroupDto): string {
        return trans.orderDetails.consumptionInclVat.replace("{0}", this.getVatPercentValueAsString(vatGroup.vatValue));
    }

    private reloadCurrentOrder() {
        this.loadOrder(this.orderId);
    }

    private initOrderInfoHeaderComponent(order: IFullOrder) {
        if (this.orderInfoHeaderComponent) {
            this.orderInfoHeaderComponent.unmount();
        }
        this.$summaryHeader!.empty();

        const props = {
            order: order,
        };
        const result = createVueComponent(OrderInfoHeader, props, this.$summaryHeader![0], true);
        this.orderInfoHeaderComponent = result.componentApp;
    }

    private async canOrderBeCanceled(orderId: string, locationId: string): Promise<boolean> {
        try {
            const canBeCanceled = await this.customerDao.getCanOrderBeCanceled(orderId, {
                locationId: locationId,
                orderId: orderId,
            });
            return canBeCanceled;
        } catch (error) {
            logError(error);
        }

        return false;
    }

    private openCancelWholeOrderModal(order: IFullOrder) {
        const result = createVueComponent(
            CancelWholeOrderModal,
            {
                orderId: order.id,
                locationId: order.locationId,
                onOrderCancellationSucceeded: () => {
                    this.onOrderCancelled();
                    this.loadOrder(this.orderId);
                },
            },
            createEmptyDiv(),
            true
        );
        const popup = result.mountedComponent;
        popup.show();
    }

    private openEditOrderPopup(orderId: string) {
        const result = createVueComponent(
            EditPreorderPopup,
            {
                orderId: orderId,
                onPreorderUpdated: async () => {
                    await this.loadOrder(this.orderId);
                },
            },
            createEmptyDiv(),
            true
        );
        const popup = result.mountedComponent;
        popup.show();
    }
}
